// Entrada liviana de la impresión: es lo único que entra en la primera carga de la app. El núcleo vive en el paquete
// @reparto/impresion (lo comparte con el panel); acá se reexporta. La plantilla, el armado ESC/POS, el Bluetooth y el
// PDF se cargan con import() la primera vez que se imprime.
import type { ContextoComprobante, DatosEtiquetaPedido } from '@reparto/impresion/plantilla';
import type { ResultadoImpresion } from '@reparto/impresion';
import type { ComprobanteImprimible } from '@reparto/shared/nucleo';
import type { Parada } from '../datos/tipos';

export { avisoImpresion, leerConfiguracionImpresora, type ResultadoImpresion } from '@reparto/impresion';
export type { ContextoComprobante } from '@reparto/impresion/plantilla';

/** Imprime el comprobante de una parada resuelta. Nunca lanza: sin impresora o si falla, sale por PDF. */
export async function imprimirComprobante(
  parada: Parada,
  contexto: ContextoComprobante,
  opciones: { permitirElegir?: boolean } = {},
): Promise<ResultadoImpresion> {
  try {
    const { impresora } = await import('./impresora');
    return await impresora.imprimirComprobante(parada, contexto, opciones);
  } catch (e) {
    return { via: 'ninguna', error: e instanceof Error ? e.message : 'no se pudo preparar' };
  }
}

/**
 * Imprime un comprobante de la facturación (decisión D57): la factura, nota de venta o comprobante interno que sale al
 * entregar (o el que ya tenía el pedido, para reimprimirlo). Nunca lanza: sin impresora sale por PDF.
 */
export async function imprimirComprobanteFiscal(
  c: ComprobanteImprimible,
  opciones: { permitirElegir?: boolean } = {},
): Promise<ResultadoImpresion> {
  try {
    const { impresora } = await import('./impresora');
    return await impresora.imprimirFiscal(c, opciones);
  } catch (e) {
    return { via: 'ninguna', error: e instanceof Error ? e.message : 'no se pudo preparar' };
  }
}

/** Imprime la etiqueta de un pedido para sus bultos (decisión D54). Nunca lanza: sin impresora sale por PDF. */
export async function imprimirEtiqueta(
  datos: Omit<DatosEtiquetaPedido, 'enlace'> & { pedidoId: string },
  opciones: { permitirElegir?: boolean } = {},
): Promise<ResultadoImpresion> {
  try {
    const { impresora } = await import('./impresora');
    return await impresora.imprimirEtiqueta(datos, opciones);
  } catch (e) {
    return { via: 'ninguna', error: e instanceof Error ? e.message : 'no se pudo preparar' };
  }
}
